'use client';

import React, { useEffect, useState } from 'react';
import Image from 'next/image';
import styled from 'styled-components';
import { statics } from '@/data/store';
import FilterTable from './FilterTable';
import LoadingOverlay from '@/components/LoadingOverlay';
import { COLORS } from '@/constants/colors';

const tableHeader = [
  {
    id: 0,
    label: 'Filter Number',
    name: 'gfu_part_num',
    maxWidth: 156,
  },
  {
    id: 1,
    label: 'Length (L)',
    name: 'filter_length_inch',
    maxWidth: 270,
  },
  {
    id: 2,
    label: 'Width (W)',
    name: 'filter_width_inch',
    maxWidth: 249,
  },
  {
    id: 3,
    label: 'Height (H)',
    name: 'filter_height_inch',
    maxWidth: 139,
  },
  {
    id: 4,
    label: 'Type',
    name: 'filter_style',
    maxWidth: 126,
  },
  {
    id: 5,
    label: 'Weight',
    name: 'filter_weight_lbs',
    maxWidth: 136,
  },
];

const UPElement1 = () => {
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getTableData();
  }, []);

  const getTableData = async () => {
    setLoading(true);
    const product_type_id = 2;

    const url = `${statics.BaseUrl}/product?product_type_id=${product_type_id}&pageNo=1&pageSize=200&is_active=1`;

    try {
      const res = await fetch(url);
      const data = await res.json();
      if (data?.ResponseCode === 'Success') {
        const rows = Array.isArray(data?.data) ? data.data : data?.data?.rows || [];
        setTableData(rows);
      }
    } catch (error) {
      console.error('Failed to fetch panel filters:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <LoadingOverlay loading={loading} />
      <UPElement1Wrapper>
        <PageTitle>Find a Panel Filter</PageTitle>

        <IntroText>
          Browse our complete selection of universal panel filters designed for high-performance air filtration.
          All Green Filter panel filters are washable, reusable, and made in the USA with our lifetime warranty.
        </IntroText>

        <div
          style={{
            textAlign: 'center',
            float: 'left',
            width: '30%',
            minWidth: 300,
          }}
        >
          <Image className="resize" alt="Panel Filter" src="/assets/panel.webp" width={338} height={200} />
        </div>

        <div style={{ clear: 'both', display: 'table' }}></div>
        <br />

        <h4 style={{ padding: '0 10px' }}>
          Select a filter from the list below. The list can be sorted by clicking on a column heading. Clicking the heading again will sort it in the opposite direction. The table will scroll left and right for smaller screens.
        </h4>
        <p style={{ textAlign: 'center' }}>
          <strong>All measurements are in inches.</strong>
        </p>

        <FilterTable headers={tableHeader} tableData={tableData} />

        <ClosingSection>
          <ClosingText>
            Need help finding the right panel filter? Use our{' '}
            <a href="/shop-by-vehicle">vehicle selector</a> or{' '}
            <a href="/contact-us">contact us</a> for expert guidance on panel filters.
          </ClosingText>
        </ClosingSection>
      </UPElement1Wrapper>
    </>
  );
};

export default React.memo(UPElement1);

const IntroText = styled.p`
  max-width: 800px;
  margin: 0 auto 30px;
  padding: 0 20px;
  font-family: Lato, sans-serif;
  font-size: 16px;
  font-weight: 400;
  color: #555;
  line-height: 1.7;
  text-align: center;

  @media (max-width: 767px) {
    font-size: 15px;
    margin-bottom: 20px;
  }
`;

const ClosingSection = styled.div`
  max-width: 800px;
  margin: 30px auto 50px;
  padding: 20px;
  background: #f9f9f9;
  border-radius: 8px;
  text-align: center;

  @media (max-width: 767px) {
    margin: 20px auto 30px;
    padding: 15px;
  }
`;

const ClosingText = styled.p`
  font-family: Lato, sans-serif;
  font-size: 15px;
  color: #555;
  margin: 0;
  line-height: 1.6;

  a {
    color: ${COLORS.brandGreen};
    text-decoration: none;
    font-weight: 600;

    &:hover {
      text-decoration: underline;
    }
  }

  @media (max-width: 767px) {
    font-size: 14px;
  }
`;

const PageTitle = styled.h1`
  font-family: Lato, sans-serif;
  font-size: 38px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 20px 20px;

  @media (max-width: 767px) {
    font-size: 26px;
    margin: 0 0 16px;
  }
`;

const UPElement1Wrapper = styled.div`
  padding: 20px;
  background: #fff;

  h2 {
    font-family: Lato, sans-serif;
    font-size: 28px;
    font-weight: 700;
    color: #1a1a1a;
    margin-bottom: 30px;
  }

  h4 {
    font-family: Lato, sans-serif;
    font-size: 16px;
    font-weight: 400;
    color: #333;
    line-height: 1.6;
    margin-bottom: 20px;
  }

  p {
    font-family: Lato, sans-serif;
    font-size: 15px;
    color: #333;
    margin-bottom: 30px;
  }

  .resize {
    max-width: 100%;
    height: auto;
  }

  @media (max-width: 767px) {
    padding: 15px;

    h2 {
      font-size: 22px;
    }

    h4 {
      font-size: 14px;
    }

    div[style*='float: left'] {
      float: none !important;
      width: 100% !important;
      min-width: auto !important;
      margin-bottom: 20px;
    }
  }
`;
